import React from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail } from "lucide-react";
import { Logo } from "./Logo";
import { LinkedInButton } from "@/components/ui/LinkedInButton";
import { useSettings } from "@/context/SettingsContext";
import { useFetch } from "@/hooks/useFetch";
import { localizedPath, swapLang, pick } from "@/lib/helpers";
import { setLangCookie } from "@/i18n";

const NAV = [
  { key: "home", path: "/" },
  { key: "services", path: "/services" },
  { key: "about", path: "/about" },
  { key: "contact", path: "/contact" },
];

export function Footer() {
  const { t, i18n } = useTranslation();
  const { lang = "en" } = useParams();
  const location = useLocation();
  const { settings } = useSettings();
  const { data: globalPage } = useFetch("/pages/global", []);
  const tagline = pick(globalPage?.footerDescription, lang) || pick({ en: settings.tagline_en, fr: settings.tagline_fr }, lang);
  const email = settings.contactEmail;

  return (
    <footer className="bg-surface border-t border-hair" data-testid="site-footer">
      <div className="container-x py-16">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <Logo />
            {tagline ? <p className="mt-5 text-mut max-w-sm leading-relaxed">{tagline}</p> : null}
          </div>

          <div className="md:col-span-3">
            <h3 className="label-tech mb-4">{t("footer.pages")}</h3>
            <ul className="space-y-3">
              {NAV.map((item) => (
                <li key={item.key}>
                  <Link
                    to={localizedPath(lang, item.path)}
                    data-testid={`footer-nav-${item.key}`}
                    className="text-fg hover:text-accent transition-colors"
                  >
                    {t(`nav.${item.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h3 className="label-tech mb-4">{t("footer.connect")}</h3>
            {email ? (
              <a
                href={`mailto:${email}`}
                data-testid="footer-email"
                className="inline-flex items-center gap-2 text-fg hover:text-accent transition-colors"
              >
                <Mail className="w-4 h-4" /> {email}
              </a>
            ) : null}
            <div className="mt-5 flex items-center gap-3">
              <LinkedInButton url={settings.linkedinUrl} label={t("nav.linkedin")} />
              <Link
                to={swapLang(location.pathname, lang === "en" ? "fr" : "en")}
                onClick={() => {
                  const to = lang === "en" ? "fr" : "en";
                  setLangCookie(to);
                  i18n.changeLanguage(to);
                }}
                data-testid="footer-lang-switch"
                className="h-11 px-4 grid place-items-center border border-hair text-mut font-techmono text-xs uppercase tracking-wider hover:border-cyan hover:text-accent transition-colors"
              >
                {lang === "en" ? "FR" : "EN"}
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-hair flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-mut text-sm font-techmono">
            © {new Date().getFullYear()} {(pick({ en: settings.siteName_en, fr: settings.siteName_fr }, lang) || "").replace(/\.+$/, "")}. {t("footer.rights")}
          </p>
          <Link to={localizedPath(lang, "/privacy")} className="text-mut text-sm hover:text-accent transition-colors" data-testid="footer-privacy">
            {t("footer.privacy")}
          </Link>
        </div>
      </div>
    </footer>
  );
}
